import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import CognitoAuthService, {
  type CognitoAuthResponse,
} from '../utils/cognitoAuth';
import { clearTenantColors } from '../utils/colorUtils';
import useTenantStore from '../stores/TenantStore';

interface User {
  email: string;
  sub: string;
  [key: string]: unknown;
}

interface CognitoAuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // Login flow state
  loginStep: 'email' | 'otp';
  loginEmail: string;
  loginSession: string;
  loginOtp: string;
  setLoginStep: (step: 'email' | 'otp') => void;
  setLoginEmail: (email: string) => void;
  setLoginSession: (session: string) => void;
  setLoginOtp: (otp: string) => void;
  // Login methods
  login: (email: string, otpCode: string, session: string) => Promise<void>;
  initiateLogin: (email: string) => Promise<{ session: string }>;
  logout: () => void;
  getAccessToken: () => string | null;
  getIdToken: () => string | null;
}

const CognitoAuthContext = createContext<CognitoAuthContextType | null>(null);

const TOKEN_KEY = 'cognito_tokens';
const CLIENT_ID = process.env.REACT_APP_AWS_COGNITO_CLIENT_ID;

if (!CLIENT_ID) {
  throw new Error(
    'REACT_APP_AWS_COGNITO_CLIENT_ID environment variable is required',
  );
}

export const CognitoAuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { clearTenant } = useTenantStore();
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Login flow state
  const [loginStep, setLoginStep] = useState<'email' | 'otp'>('email');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginSession, setLoginSession] = useState('');
  const [loginOtp, setLoginOtp] = useState('');

  const authService = new CognitoAuthService(CLIENT_ID);

  // Load tokens from localStorage on mount
  useEffect(() => {
    const loadTokens = () => {
      try {
        const storedTokens = localStorage.getItem(TOKEN_KEY);
        if (storedTokens) {
          const tokens: CognitoAuthResponse = JSON.parse(storedTokens);
          if (tokens.IdToken) {
            const userData = authService.parseJwtToken(tokens.IdToken);
            if (userData?.exp && Date.now() < Number(userData.exp) * 1000) {
              setUser({
                email: userData.email as string,
                sub: userData.sub as string,
                ...userData,
              });
              setIsAuthenticated(true);
            } else {
              // Token expired, try to refresh
              if (tokens.RefreshToken) {
                refreshAuthToken(tokens.RefreshToken).catch(() => {
                  // Refresh failed, clear tokens
                  localStorage.removeItem(TOKEN_KEY);
                });
              } else {
                localStorage.removeItem(TOKEN_KEY);
              }
            }
          }
        }
      } catch (err) {
        setError('Failed to load authentication state');
        localStorage.removeItem(TOKEN_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    loadTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAuthToken = async (refreshToken: string) => {
    try {
      const response = await authService.refreshToken(refreshToken);
      if (response.IdToken) {
        const userData = authService.parseJwtToken(response.IdToken);
        if (userData) {
          const tokens = {
            ...response,
            RefreshToken: refreshToken, // Keep the existing refresh token
          };
          localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
          setUser({
            email: userData.email as string,
            sub: userData.sub as string,
            ...userData,
          });
          setIsAuthenticated(true);
        }
      }
    } catch (err) {
      throw new Error('Failed to refresh token');
    }
  };

  const initiateLogin = async (email: string): Promise<{ session: string }> => {
    try {
      setError(null);
      setIsLoading(true);

      // Skip SignUp entirely - users already exist in Cognito
      // Go directly to InitiateAuth to get OTP
      const response = await authService.initiateAuth(email);

      if (response.Session) {
        // Update login flow state
        setLoginEmail(email);
        setLoginSession(response.Session);
        setLoginStep('otp');
        setLoginOtp(''); // Clear any previous OTP

        return { session: response.Session };
      }

      throw new Error(
        'Failed to initiate authentication - no session received',
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Authentication failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (
    email: string,
    otpCode: string,
    session: string,
  ): Promise<void> => {
    try {
      setError(null);
      setIsLoading(true);

      const response = await authService.respondToAuthChallenge(
        email,
        otpCode,
        session,
      );

      if (response.AuthenticationResult?.IdToken) {
        const tokens = response.AuthenticationResult;
        localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));

        if (tokens.IdToken) {
          const userData = authService.parseJwtToken(tokens.IdToken);
          if (userData) {
            setUser({
              email: userData.email as string,
              sub: userData.sub as string,
              ...userData,
            });
            setIsAuthenticated(true);

            // Reset login flow state
            setLoginStep('email');
            setLoginEmail('');
            setLoginSession('');
            setLoginOtp('');
          }
        }
      } else {
        throw new Error('Authentication failed');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Authentication failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('selected_tenant'); // Clear selected tenant
    localStorage.removeItem('tenant-storage'); // Clear tenant store persistence
    clearTenant(); // Clear tenant store state
    clearTenantColors();
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  };

  const getAccessToken = (): string | null => {
    try {
      const storedTokens = localStorage.getItem(TOKEN_KEY);
      if (storedTokens) {
        const tokens: CognitoAuthResponse = JSON.parse(storedTokens);
        return tokens.AccessToken ?? null;
      }
    } catch (err) {
      // Error getting token
    }
    return null;
  };

  const getIdToken = (): string | null => {
    try {
      const storedTokens = localStorage.getItem(TOKEN_KEY);
      if (storedTokens) {
        const tokens: CognitoAuthResponse = JSON.parse(storedTokens);
        return tokens.IdToken ?? null;
      }
    } catch (err) {
      // Error getting token
    }
    return null;
  };

  const value: CognitoAuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    // Login flow state
    loginStep,
    loginEmail,
    loginSession,
    loginOtp,
    setLoginStep,
    setLoginEmail,
    setLoginSession,
    setLoginOtp,
    // Login methods
    login,
    initiateLogin,
    logout,
    getAccessToken,
    getIdToken,
  };

  return (
    <CognitoAuthContext.Provider value={value}>
      {children}
    </CognitoAuthContext.Provider>
  );
};

export const useCognitoAuth = (): CognitoAuthContextType => {
  const context = useContext(CognitoAuthContext);
  if (!context) {
    throw new Error('useCognitoAuth must be used within a CognitoAuthProvider');
  }
  return context;
};
